/**
 * @fileoverview Feature flags composable for gating access to features like
 * Dashboard Builder.
 *
 * Feature availability is determined by two sources, combined with OR logic:
 *
 * 1. **Global config** — a static JS config file (`src/config/feature-flags.config.js`)
 *    that ships with each deployment. A flag can be:
 *    - `true` for all users
 *    - `false` for entitlement-only access
 *    - `'internal'` for users on a domain in `INTERNAL_FEATURE_EMAIL_DOMAINS`
 *      (`src/utils/featureFlags.js`: profiteia.io, bakeable.nl, giga-storage.com) only
 * 2. **User-based entitlements** — the user's `activeEntitlements` array from
 *    the Vuex auth store (provided by the backend).  This allows giving
 *    individual users early access when the global flag is off.
 *
 * Super users always have access regardless of flags.
 */

import featureFlagsConfig from '@/app/feature-flags';
import { useAuthStore } from '@/stores/useAuthStore';
import {
  FEATURE_FLAGS,
  isFeatureEnabled as resolveFeatureFlag,
  isFeatureGloballyEnabled,
} from '@/utils/featureFlags';
import { computed } from 'vue';

export { FEATURE_FLAGS } from '@/utils/featureFlags';

/**
 * Composable for checking feature flags in Vue components.
 *
 * Combines the static global config with user entitlements (Vuex auth store)
 * to determine whether a feature is enabled.  Super users bypass all checks.
 *
 * @example
 * ```vue
 * <script setup>
 * import { useFeatureFlags } from '@/composables/useFeatureFlags'
 *
 * const { isDashboardBuilderEnabled } = useFeatureFlags()
 * </script>
 *
 * <template>
 *   <div v-if="isDashboardBuilderEnabled">Dashboard Builder</div>
 * </template>
 * ```
 *
 * @returns {{
 *   isDashboardBuilderEnabled: import('vue').ComputedRef<boolean>,
 *   isKpiVisualizationsEnabled: import('vue').ComputedRef<boolean>,
 *   isCapacityFeeForecasterEnabled: import('vue').ComputedRef<boolean>,
 *   isFeatureEnabled: (featureKey: string) => boolean
 * }}
 */
export function useFeatureFlags() {
  const authStore = useAuthStore();

  /**
   * Check if a feature is enabled via the global config, a user entitlement,
   * or super-user status.
   * @param {string} featureKey - The feature flag key (e.g. 'dashboard-builder')
   * @returns {boolean} True if the feature is enabled
   */
  const isFeatureEnabled = (featureKey) => {
    return resolveFeatureFlag(featureKey, featureFlagsConfig, authStore.$state || {});
  };

  /**
   * Whether the Dashboard Builder feature is enabled
   */
  const isDashboardBuilderEnabled = computed(() =>
    isFeatureEnabled(FEATURE_FLAGS.DASHBOARD_BUILDER)
  );

  const isKpiVisualizationsEnabled = computed(() =>
    isFeatureEnabled(FEATURE_FLAGS.KPI_VISUALIZATIONS)
  );
  const isApiDocsCatalogEnabled = computed(() => isFeatureEnabled(FEATURE_FLAGS.API_DOCS_CATALOG));
  const isPowerMarketSimulatorEnabled = computed(() =>
    isFeatureEnabled(FEATURE_FLAGS.POWER_MARKET_SIMULATOR)
  );
  const isEnvironmentAssetsEnabled = computed(() =>
    isFeatureGloballyEnabled(FEATURE_FLAGS.ENVIRONMENT_ASSETS, featureFlagsConfig, authStore.$state || {})
  );
  const isShadowTradingEnabled = computed(() => isFeatureEnabled(FEATURE_FLAGS.SHADOW_TRADING));
  const isCapacityFeeForecasterEnabled = computed(() =>
    isFeatureEnabled(FEATURE_FLAGS.CAPACITY_FEE_FORECASTER)
  );
  const isSpikeProbabilityEnabled = computed(() =>
    isFeatureEnabled(FEATURE_FLAGS.SPIKE_PROBABILITY)
  );
  const isChangelogDialogEnabled = computed(() =>
    isFeatureEnabled(FEATURE_FLAGS.CHANGELOG_DIALOG)
  );
  const isAlertRegistryAllowlistEnabled = computed(() =>
    isFeatureEnabled(FEATURE_FLAGS.ALERT_REGISTRY_ALLOWLIST)
  );

  return {
    isDashboardBuilderEnabled,
    isKpiVisualizationsEnabled,
    isApiDocsCatalogEnabled,
    isPowerMarketSimulatorEnabled,
    isEnvironmentAssetsEnabled,
    isShadowTradingEnabled,
    isChangelogDialogEnabled,
    isCapacityFeeForecasterEnabled,
    isSpikeProbabilityEnabled,
    isAlertRegistryAllowlistEnabled,
    isFeatureEnabled,
  };
}
